import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Target, Eye, Heart, Award, Users, Clock, Truck, Shield } from "lucide-react";

const values = [
  { icon: Target, title: "Precision", desc: "Every detail matters. We measure twice and cut once to deliver flawless results." },
  { icon: Eye, title: "Innovation", desc: "We stay ahead with the latest signage technology and LED display solutions." },
  { icon: Heart, title: "Dedication", desc: "Your success is our mission. We go above and beyond for every client." },
  { icon: Award, title: "Quality", desc: "Premium materials and expert craftsmanship in every project we deliver." },
];

const processSteps = [
  { num: "01", title: "Initial Consultation", desc: "You reach out and tell us what kind of sign you need. We send you samples, arrange an in-person visit, or conduct a free on-site survey to assess your location and requirements." },
  { num: "02", title: "Estimate & Deposit", desc: "We provide a detailed estimate tailored to your project. Once approved, a 50% deposit is required to get started. This secures your production slot and kicks off the design phase." },
  { num: "03", title: "Custom Design", desc: "Our design team creates a custom concept aligned with your brand. We include up to two rounds of revisions to ensure the design is exactly what you envisioned before moving forward." },
  { num: "04", title: "Design Approval", desc: "You review and approve the final design. This is a critical step. production only begins after your sign-off, ensuring we build exactly what you want with zero surprises." },
  { num: "05", title: "Fabrication", desc: "Our team begins building your sign. Simple signs like vinyl prints and composites take 2-5 business days. Larger projects like channel letters take approximately 2 weeks." },
  { num: "06", title: "Review & Installation", desc: "Once complete, visit our shop to inspect your sign and settle the remaining balance. If you can't visit, we send photos for approval. After sign-off, we schedule installation at your convenience." },
];

const highlights = [
  { icon: Users, value: "150+", label: "Clients Served" },
  { icon: Clock, value: "8+", label: "Years in Business" },
  { icon: Truck, value: "Pro", label: "Installation Team" },
  { icon: Shield, value: "10 Year", label: "Customer Service" },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-gold text-sm tracking-[0.3em] uppercase font-semibold mb-4">
            About Us
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-heading text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter uppercase text-white mb-6">
            Crafting Signs<br />Since <span className="gold-shimmer">2017</span>
          </motion.h1>
        </div>
      </section>

      {/* Story */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                Vancouver's Trusted Signage Partner
              </h2>
              <div className="space-y-4 text-white/60 leading-relaxed">
                <p>
                  VANSIGN was founded with a simple mission: to help Vancouver businesses stand out. What started as a small workshop has grown into one of the city's most trusted signage companies.
                </p>
                <p>
                  We specialize in premium LED displays, channel letters, neon signs, window graphics, car wrapping, lightboxes, and pylon signs. Every project receives our full attention, from initial design consultation to final installation.
                </p>
                <p>
                  Our commitment goes beyond just creating signs. We provide professional installation, comprehensive warranties extendable up to 10 years, and dedicated customer service. We serve a wide range of businesses - from supermarkets and retail stores to restaurants, cafes, and corporate offices.
                </p>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1555392487-1512c05bb4fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHx2YW5jb3V2ZXIlMjBza3lsaW5lJTIwbmlnaHQlMjBjaXR5JTIwbGlnaHRzfGVufDB8fHx8MTc3MzYwMDM2OHww&ixlib=rb-4.1.0&q=85"
                  alt="Vancouver skyline"
                  className="w-full h-[400px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-gold text-black p-6 rounded-2xl">
                <p className="font-heading text-3xl font-bold">500+</p>
                <p className="text-xs font-semibold">Projects Delivered</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 section-alt border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {highlights.map((h, i) => (
            <motion.div
              key={h.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="text-center"
            >
              <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4">
                <h.icon className="w-5 h-5 text-gold" />
              </div>
              <p className="font-heading text-2xl md:text-3xl font-bold text-gold">{h.value}</p>
              <p className="text-sm text-white/50 mt-1">{h.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="py-28 md:py-40" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <p className="text-gold text-sm tracking-[0.3em] uppercase font-semibold mb-4">Our Values</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white">What Drives Us</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card rounded-2xl p-8 text-center"
              >
                <div className="w-14 h-14 rounded-xl bg-gold/10 flex items-center justify-center mx-auto mb-6">
                  <v.icon className="w-6 h-6 text-gold" />
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-3">{v.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-28 md:py-40 section-alt" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <p className="text-gold text-sm tracking-[0.3em] uppercase font-semibold mb-4">Your Journey</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white">From Concept to Installation</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="relative"
              >
                <span className="font-heading text-6xl font-bold text-white/5">{step.num}</span>
                <h3 className="font-heading text-xl font-bold text-white mt-2 mb-3">{step.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{step.desc}</p>
                {i < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 right-0 w-16 gold-line"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Work With Us?
          </h2>
          <p className="text-white/50 text-lg mb-10">
            Join over 150 businesses who trust VANSIGN for their signage needs.
          </p>
          <Link to="/booking" data-testid="about-cta-btn" className="inline-flex items-center gap-2 bg-gold text-black font-bold px-8 py-4 rounded-full hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all duration-300 hover:-translate-y-1">
            Get Started <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
